import { useState } from "react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import type { QueueItem, QueueItemStatus, UploadQueueApi } from "./useUploadQueue";

/**
 * UploadQueuePanel — docked UI for the sequential upload queue
 * (`useUploadQueue`). Shown whenever the queue is non-empty; sits
 * bottom-right at a z-index below the app's modals.
 *
 * Per item it surfaces status, a progress bar for the active upload,
 * and the contextual action: re-pick a file for a post-reload item,
 * cancel the active upload, retry a failed one, or remove a finished
 * one. The file `accept` filter mirrors `CoachMatchUploadModal`.
 */

const ACCEPT = ".mp4,.mkv,video/mp4,video/x-matroska";

/** Above this many uploaded chunks, cancelling the active item asks
 * for confirmation — mirrors `CONFIRM_AFTER_CHUNKS` in the modal. */
const CONFIRM_AFTER_CHUNKS = 2;

function slotLabel(slot: string): string {
  return slot.replace("_", " ");
}

function statusTone(status: QueueItemStatus): string {
  switch (status) {
    case "done":
      return "border-good/40 text-good";
    case "uploading":
      return "border-accent/40 text-accent";
    case "error":
      return "border-destructive/40 text-destructive";
    case "cancelled":
      return "border-rule text-ink-muted";
    default:
      return "border-rule text-ink-muted";
  }
}

export function UploadQueuePanel({ queue }: { queue: UploadQueueApi }) {
  const { items, activeState, activeId } = queue;
  const [collapsed, setCollapsed] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  if (items.length === 0) return null;

  const doneCount = items.filter((it) => it.status === "done").length;
  const finishedCount = items.filter(
    (it) => it.status === "done" || it.status === "cancelled" || it.status === "error",
  ).length;

  const cancelWouldDiscardWork =
    activeState.phase === "uploading"
    && activeState.currentChunkIndex >= CONFIRM_AFTER_CHUNKS;

  const requestCancelActive = () => {
    if (cancelWouldDiscardWork) {
      setConfirmCancelOpen(true);
      return;
    }
    queue.cancelActive();
  };

  return (
    <div
      data-testid="upload-queue-panel"
      className="fixed inset-x-3 bottom-3 z-[120] flex max-h-[calc(100dvh-2rem)] flex-col rounded-xl border border-rule bg-surface shadow-2xl sm:left-auto sm:w-80"
    >
      <div className="flex items-center justify-between gap-2 border-b border-rule px-3 py-2">
        <h3 className="text-xs font-display uppercase tracking-wider text-ink">
          Uploads — {doneCount}/{items.length} done
        </h3>
        <div className="flex items-center gap-1">
          {finishedCount > 0 ? (
            <button
              type="button"
              onClick={queue.clearFinished}
              data-testid="upload-queue-clear-finished"
              className="rounded border border-rule px-1.5 py-0.5 text-[0.6rem] font-display uppercase tracking-wide text-ink-muted hover:text-ink"
            >
              Clear finished
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            aria-expanded={!collapsed}
            data-testid="upload-queue-collapse"
            className="rounded border border-rule px-1.5 py-0.5 font-mono text-[0.65rem] text-ink-muted hover:text-ink"
          >
            {collapsed ? "▲" : "▼"}
          </button>
        </div>
      </div>

      {collapsed ? null : (
        <ul
          data-testid="upload-queue-list"
          className="flex max-h-80 flex-col gap-1 overflow-y-auto p-2"
        >
          {items.map((item) => (
            <QueueRow
              key={item.id}
              item={item}
              isActive={item.id === activeId}
              percent={
                item.id === activeId && activeState.totalBytes > 0
                  ? Math.min(
                      100,
                      Math.round(
                        (activeState.bytesUploaded / activeState.totalBytes)
                          * 100,
                      ),
                    )
                  : null
              }
              onRepick={(file) => queue.repickFile(item.id, file)}
              repickHint={`${item.fileName} · ${(item.fileSize / 1024 / 1024).toFixed(1)} MB`}
              onRemove={() => queue.removeItem(item.id)}
              onRetry={() => queue.retryItem(item.id)}
              onCancel={requestCancelActive}
            />
          ))}
        </ul>
      )}

      {confirmCancelOpen ? (
        <ConfirmDialog
          title="Cancel upload?"
          message="Cancelling will discard this upload's progress and delete the partial file from the server. The next queued upload will start."
          confirmLabel="Cancel upload"
          cancelLabel="Keep uploading"
          danger
          data-testid="upload-queue-confirm-cancel"
          onConfirm={() => {
            setConfirmCancelOpen(false);
            queue.cancelActive();
          }}
          onClose={() => setConfirmCancelOpen(false)}
        />
      ) : null}
    </div>
  );
}

function QueueRow({
  item,
  isActive,
  percent,
  onRepick,
  repickHint,
  onRemove,
  onRetry,
  onCancel,
}: {
  item: QueueItem;
  isActive: boolean;
  percent: number | null;
  /** Returns false when the picked file doesn't match the queued
   * item's recorded filename + size. */
  onRepick: (file: File) => boolean;
  /** "<filename> · <size> MB" — shown when a re-pick is rejected so the
   * operator can see which exact file the row expects. */
  repickHint: string;
  onRemove: () => void;
  onRetry: () => void;
  onCancel: () => void;
}) {
  const needsFile = item.status === "pending" && item.file === null;
  // A mismatched re-pick (different filename / size) is a normal
  // operator slip — surface it inline instead of leaving the button
  // looking dead. Cleared as soon as a correct file is supplied.
  const [repickError, setRepickError] = useState<string | null>(null);

  return (
    <li
      data-testid="upload-queue-item"
      data-item-id={item.id}
      data-status={item.status}
      className="flex flex-col gap-1 rounded border border-rule bg-bg-card/40 px-2 py-1.5"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-xs text-ink">{item.matchLabel}</p>
          <p className="truncate font-mono text-[0.6rem] text-ink-muted">
            {slotLabel(item.slot)} · {item.fileName}
          </p>
        </div>
        <span
          data-testid="upload-queue-item-status"
          className={`shrink-0 rounded border bg-bg-card/40 px-1.5 py-0.5 text-[0.55rem] font-mono uppercase ${statusTone(
            item.status,
          )}`}
        >
          {item.status}
        </span>
      </div>

      {isActive && percent !== null ? (
        <div data-testid="upload-queue-item-progress" className="flex flex-col gap-0.5">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-card">
            <div
              className="h-full bg-accent transition-all"
              style={{ width: `${percent}%` }}
            />
          </div>
          <span className="font-mono text-[0.55rem] text-ink-muted">
            {percent}%
          </span>
        </div>
      ) : null}

      {item.status === "error" && item.error ? (
        <p
          role="alert"
          data-testid="upload-queue-item-error"
          className="text-[0.6rem] text-destructive"
        >
          {item.error}
        </p>
      ) : null}

      {repickError ? (
        <p
          role="alert"
          data-testid="upload-queue-item-repick-error"
          className="text-[0.6rem] text-destructive"
        >
          {repickError}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-1">
        {needsFile ? (
          <label className="cursor-pointer rounded border border-accent/50 bg-accent/15 px-1.5 py-0.5 text-[0.6rem] font-display uppercase tracking-wide text-accent hover:bg-accent/25">
            Choose file
            <input
              type="file"
              accept={ACCEPT}
              data-testid="upload-queue-item-repick"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  if (onRepick(file)) {
                    setRepickError(null);
                  } else {
                    setRepickError(
                      `That file doesn't match this upload. Expected ${repickHint}.`,
                    );
                  }
                }
                e.target.value = "";
              }}
            />
          </label>
        ) : null}
        {item.status === "uploading" ? (
          <button
            type="button"
            onClick={onCancel}
            data-testid="upload-queue-item-cancel"
            className="rounded border border-destructive/40 px-1.5 py-0.5 text-[0.6rem] font-display uppercase tracking-wide text-destructive hover:bg-destructive/10"
          >
            Cancel
          </button>
        ) : null}
        {item.status === "error" ? (
          <button
            type="button"
            onClick={onRetry}
            data-testid="upload-queue-item-retry"
            className="rounded border border-accent/50 bg-accent/15 px-1.5 py-0.5 text-[0.6rem] font-display uppercase tracking-wide text-accent hover:bg-accent/25"
          >
            Retry
          </button>
        ) : null}
        {item.status !== "uploading" ? (
          <button
            type="button"
            onClick={onRemove}
            data-testid="upload-queue-item-remove"
            className="rounded border border-rule px-1.5 py-0.5 text-[0.6rem] font-display uppercase tracking-wide text-ink-muted hover:text-ink"
          >
            Remove
          </button>
        ) : null}
      </div>
    </li>
  );
}
